import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import PartnerMainContainer from '../../../components/PartnerMainContainer';
import { Box } from '@mui/material';
import PartnerPageService from '../../../services/PartnerPageService';

const TOC_SECTIONS = [
  {
    title: 'Démarrage',
    items: [
      { id: 'connexion', label: 'Connexion & rôles' },
      { id: 'interface', label: 'Interface générale' },
    ],
  },
  {
    title: 'Données',
    items: [
      { id: 'import', label: 'Importer une base Excel' },
      { id: 'import-structure', label: 'Structure du fichier', isSub: true },
      { id: 'sync', label: "Synchroniser les bases" },
    ],
  },
  {
    title: 'Navigation',
    items: [
      { id: 'dashboard', label: 'Tableau de bord' },
      { id: 'chronologie', label: 'Chronologie', isSub: true },
      { id: 'mouvements', label: 'Mouvements inter-bases', isSub: true },
      { id: 'partenaires', label: 'Partenaires' },
      { id: 'fiche-part', label: 'Fiche partenaire', isSub: true },
      { id: 'marches', label: 'Marchés' },
      { id: 'evaluations', label: 'Évaluations' },
      { id: 'resilies', label: 'Résiliés & Fins' },
      { id: 'personnel', label: 'Personnel' },
      { id: 'multi', label: 'Multi-partenaires' },
    ],
  },
  {
    title: 'IDs stables',
    items: [
      { id: 'ids', label: 'Registre des IDs' },
      { id: 'fusion', label: 'Fusion de noms', isSub: true },
    ],
  },
  {
    title: 'Admin',
    items: [
      { id: 'bases', label: 'Bases de données' },
    ],
  },
  {
    title: 'Référence',
    items: [
      { id: 'badges', label: 'Badges & indicateurs' },
      { id: 'colonnes', label: 'Colonnes Excel' },
    ],
  },
];

const useStyles = makeStyles((theme) => ({
  page: {
    padding: theme.spacing(1, 1, 4),
    color: '#1f2a44',
    fontFamily: "'DM Sans', sans-serif",
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '280px minmax(0, 1fr)',
    gap: theme.spacing(2.5),
    alignItems: 'start',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  toc: {
    position: 'sticky',
    top: theme.spacing(3),
    borderRadius: 24,
    border: '1px solid #dbe6f3',
    background: 'linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)',
    boxShadow: '0 18px 36px rgba(15, 23, 42, 0.08)',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      position: 'relative',
      top: 0,
    },
  },
  tocHead: {
    padding: '20px 20px 16px',
    borderBottom: '1px solid #e2e8f0',
    background: 'linear-gradient(180deg, #f9fbff 0%, #eef6ff 100%)',
  },
  tocEyebrow: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 1.6,
    color: '#64748b',
    fontWeight: 700,
    marginBottom: 8,
  },
  tocTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 20,
    lineHeight: 1.1,
    fontWeight: 800,
    color: '#16304d',
    marginBottom: 6,
  },
  tocSubTitle: {
    fontSize: 12,
    color: '#5f7593',
    lineHeight: 1.5,
  },
  tocBody: {
    padding: '10px 0 16px',
    maxHeight: 'calc(100vh - 180px)',
    overflowY: 'auto',
    [theme.breakpoints.down('md')]: {
      maxHeight: 'none',
    },
  },
  tocSectionTitle: {
    padding: '12px 20px 4px',
    fontSize: 10,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    fontWeight: 700,
  },
  tocLink: {
    display: 'block',
    padding: '8px 20px',
    fontSize: 12,
    color: '#516b89',
    textDecoration: 'none',
    borderLeft: '2px solid transparent',
    transition: 'all 160ms ease',
    '&:hover': {
      background: 'rgba(37, 99, 235, 0.06)',
      color: '#1d4ed8',
    },
  },
  tocLinkSub: {
    paddingLeft: 34,
    fontSize: 11,
  },
  tocLinkActive: {
    color: '#1d4ed8',
    borderLeftColor: '#1d4ed8',
    background: 'rgba(37, 99, 235, 0.08)',
    fontWeight: 700,
  },
  content: {
    // minWidth: 0,
  },
  pageHeader: {
    borderRadius: 28,
    border: '1px solid #dbe6f3',
    // background: 'linear-gradient(145deg, #ffffff 0%, #f4f8ff 42%, #eef6ff 100%)',
    padding: '18px 22px 14px',
    boxShadow: '0 22px 45px rgba(15, 23, 42, 0.08)',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: '24px 20px 22px',
    },
  },
  pageHeaderGlow: {
    position: 'absolute',
    inset: 'auto -10% -45% auto',
    width: 220,
    height: 220,
    borderRadius: '50%',
    // background: 'radial-gradient(circle, rgba(106,207,246,0.22) 0%, rgba(106,207,246,0) 70%)',
    pointerEvents: 'none',
  },
  docTag: {
    fontSize: 10,
    letterSpacing: 3,
    textTransform: 'uppercase',
    color: '#64748b',
    fontFamily: "'DM Mono', monospace",
    marginBottom: 10,
  },
  pageTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 30,
    lineHeight: 1.05,
    fontWeight: 700,
    color: '#16304d',
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: 30,
    },
  },
  pageTitleAccent: {
    color: '#1d4ed8',
  },
  pageSub: {
    fontSize: 12,
    color: '#5f7593',
    maxWidth: 620,
    marginTop: 8,
    // lineHeight: 1.7,
  },
  contentCard: {
    marginTop: theme.spacing(1.25),
    borderRadius: 28,
    border: '1px solid #dbe6f3',
    background: '#ffffff',
    boxShadow: '0 22px 45px rgba(15, 23, 42, 0.06)',
    padding: '8px 36px 36px',
    [theme.breakpoints.down('sm')]: {
      padding: '8px 20px 24px',
    },
  },
  sectionAnchor: {
    display: 'block',
    position: 'relative',
    top: -96,
    visibility: 'hidden',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '14px 0 18px',
    paddingTop: 8,
    color: '#16304d',
    fontFamily: "'Syne', sans-serif",
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 1.2,
    '&::before': {
      content: '""',
      display: 'block',
      width: 5,
      height: 24,
      borderRadius: 999,
      background: 'linear-gradient(180deg, #2563eb 0%, #6ACFF6 100%)',
      flexShrink: 0,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 22,
      marginTop: 40,
    },
  },
  subTitle: {
    margin: '28px 0 10px',
    color: '#1d4ed8',
    fontFamily: "'Syne', sans-serif",
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  minorTitle: {
    margin: '16px 0 8px',
    color: '#5f7593',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  paragraph: {
    margin: '0 0 12px',
    fontSize: 14,
    lineHeight: 1.8,
    color: '#4b6488',
  },
  steps: {
    margin: '18px 0',
  },
  step: {
    display: 'flex',
    gap: 14,
    alignItems: 'flex-start',
    padding: '12px 0',
  },
  stepNum: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#2563eb',
    color: '#ffffff',
    fontFamily: "'DM Mono', monospace",
    fontSize: 12,
    fontWeight: 700,
    marginTop: 2,
  },
  stepBody: {
    minWidth: 0,
  },
  stepTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#16304d',
    marginBottom: 4,
  },
  stepDesc: {
    fontSize: 13,
    lineHeight: 1.7,
    color: '#4b6488',
  },
  roleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
    margin: '18px 0',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  roleCard: {
    borderRadius: 18,
    padding: 16,
    border: '1px solid #dbe6f3',
  },
  roleCardAdmin: {
    background: 'linear-gradient(180deg, rgba(251,146,60,0.16) 0%, rgba(255,255,255,0.98) 100%)',
    borderColor: 'rgba(251,146,60,0.28)',
  },
  roleCardUser: {
    background: 'linear-gradient(180deg, rgba(79,142,247,0.14) 0%, rgba(255,255,255,0.98) 100%)',
    borderColor: 'rgba(79,142,247,0.24)',
  },
  roleCardReader: {
    background: 'linear-gradient(180deg, rgba(52,211,153,0.14) 0%, rgba(255,255,255,0.98) 100%)',
    borderColor: 'rgba(52,211,153,0.22)',
  },
  roleName: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
    fontSize: 13,
    fontWeight: 700,
    color: '#16304d',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    fontFamily: "'DM Mono', monospace",
  },
  rolePerms: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  rolePermItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontSize: 12,
    lineHeight: 1.6,
    color: '#4b6488',
    '& + &': {
      marginTop: 6,
    },
  },
  rolePermBullet: {
    color: '#10b981',
    fontWeight: 700,
    marginTop: 1,
  },
  rolePermDisabled: {
    '& $rolePermBullet': {
      color: '#94a3b8',
    },
  },
  navGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
    margin: '16px 0',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  navItem: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    padding: '14px 16px',
    borderRadius: 16,
    border: '1px solid #dbe6f3',
    background: '#f8fbff',
  },
  navItemAdmin: {
    background: 'linear-gradient(180deg, rgba(251,146,60,0.12) 0%, #fffaf5 100%)',
    borderColor: 'rgba(251,146,60,0.24)',
  },
  navIcon: {
    fontSize: 18,
    lineHeight: 1,
    marginTop: 2,
  },
  navTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#16304d',
    marginBottom: 3,
  },
  navDesc: {
    fontSize: 12,
    lineHeight: 1.6,
    color: '#5f7593',
  },
  tableWrap: {
    width: '100%',
    overflowX: 'auto',
    margin: '16px 0',
    border: '1px solid #e2e8f0',
    borderRadius: 18,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 560,
    '& th': {
      background: '#f6faff',
      padding: '10px 12px',
      textAlign: 'left',
      color: '#64748b',
      fontSize: 11,
      textTransform: 'uppercase',
      letterSpacing: 0.7,
      borderBottom: '1px solid #e2e8f0',
    },
    '& td': {
      padding: '10px 12px',
      verticalAlign: 'top',
      color: '#4b6488',
      fontSize: 13,
      lineHeight: 1.6,
      borderBottom: '1px solid #edf2f7',
    },
    '& tr:last-child td': {
      borderBottom: 'none',
    },
  },
  tableFirstCell: {
    width: 220,
    whiteSpace: 'nowrap',
    color: '#16304d !important',
    fontWeight: 700,
    fontFamily: "'DM Mono', monospace",
    fontSize: '12px !important',
  },
  callout: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    margin: '16px 0',
    padding: '14px 16px',
    borderRadius: 18,
    border: '1px solid transparent',
    fontSize: 13,
    lineHeight: 1.7,
    color: '#16304d',
  },
  calloutInfo: {
    background: 'rgba(79, 142, 247, 0.10)',
    borderColor: 'rgba(79, 142, 247, 0.22)',
  },
  calloutWarn: {
    background: 'rgba(251, 191, 36, 0.14)',
    borderColor: 'rgba(251, 191, 36, 0.28)',
  },
  calloutTip: {
    background: 'rgba(52, 211, 153, 0.12)',
    borderColor: 'rgba(52, 211, 153, 0.26)',
  },
  calloutDanger: {
    background: 'rgba(248, 113, 113, 0.12)',
    borderColor: 'rgba(248, 113, 113, 0.26)',
  },
  calloutIcon: {
    fontSize: 18,
    lineHeight: 1,
    flexShrink: 0,
    marginTop: 1,
  },
  featureBlock: {
    margin: '16px 0',
    padding: 18,
    borderRadius: 18,
    border: '1px solid #dbe6f3',
    background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)',
  },
  bulletList: {
    margin: '10px 0 10px 18px',
    padding: 0,
    color: '#4b6488',
    '& li': {
      marginBottom: 8,
      lineHeight: 1.7,
    },
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
    margin: '10px 0 12px',
  },
  badgeLine: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    margin: '12px 0',
    color: '#4b6488',
    fontSize: 13,
    lineHeight: 1.7,
  },
  badgeLineItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3px 9px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.2,
    fontFamily: "'DM Mono', monospace",
    border: '1px solid transparent',
  },
  badgeEnc: {
    color: '#059669',
    background: 'rgba(52, 211, 153, 0.16)',
    borderColor: 'rgba(52, 211, 153, 0.22)',
  },
  badgeFin: {
    color: '#475569',
    background: 'rgba(148, 163, 184, 0.18)',
  },
  badgeRes: {
    color: '#dc2626',
    background: 'rgba(248, 113, 113, 0.16)',
    borderColor: 'rgba(248, 113, 113, 0.22)',
  },
  badgeNew: {
    color: '#16a34a',
    background: 'rgba(34, 197, 94, 0.16)',
  },
  badgeMod: {
    color: '#4f46e5',
    background: 'rgba(99, 102, 241, 0.16)',
  },
  badgeMulti: {
    color: '#d97706',
    background: 'rgba(251, 191, 36, 0.18)',
  },
  badgeCi: {
    color: '#7c3aed',
    background: 'rgba(192, 132, 252, 0.18)',
  },
  badgePid: {
    color: '#2563eb',
    background: 'rgba(79, 142, 247, 0.14)',
    borderColor: 'rgba(79, 142, 247, 0.22)',
  },
  badgePeid: {
    color: '#7c3aed',
    background: 'rgba(192, 132, 252, 0.16)',
    borderColor: 'rgba(192, 132, 252, 0.22)',
  },
  badgeBp: {
    color: '#2563eb',
    background: 'rgba(79, 142, 247, 0.16)',
  },
  badgeBe: {
    color: '#059669',
    background: 'rgba(52, 211, 153, 0.16)',
  },
  columnsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '6px 20px',
    margin: '14px 0',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  columnItem: {
    position: 'relative',
    paddingLeft: 14,
    color: '#4b6488',
    fontSize: 13,
    lineHeight: 1.7,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 9,
      width: 6,
      height: 6,
      borderRadius: '50%',
      background: '#2563eb',
    },
  },
  columnItemRh: {
    '&::before': {
      background: '#7c3aed',
    },
  },
  helperLegend: {
    marginTop: 6,
    color: '#64748b',
    fontSize: 12,
  },
  footer: {
    marginTop: 34,
    paddingTop: 24,
    borderTop: '1px solid #e2e8f0',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: 11,
    fontFamily: "'DM Mono', monospace",
  },
  sectionDivider: {
    border: 'none',
    borderTop: '1px solid #e2e8f0',
    margin: '40px 0 0',
  },
}));

const BADGE_CLASS_MAP = {
  enc: 'badgeEnc',
  fin: 'badgeFin',
  res: 'badgeRes',
  new: 'badgeNew',
  mod: 'badgeMod',
  multi: 'badgeMulti',
  ci: 'badgeCi',
  pid: 'badgePid',
  peid: 'badgePeid',
  bp: 'badgeBp',
  be: 'badgeBe',
};

const CALLOUT_CLASS_MAP = {
  info: 'calloutInfo',
  warn: 'calloutWarn',
  tip: 'calloutTip',
  danger: 'calloutDanger',
};

function GuideTable({ classes, headers, rows }) {
  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`${rowIndex}-${row.length}`}>
              {row.map((cell, cellIndex) => (
                <td key={`${rowIndex}-${cellIndex}`} className={cellIndex === 0 ? classes.tableFirstCell : undefined}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GuideCallout({ classes, tone, icon, children }) {
  return (
    <div className={clsx(classes.callout, classes[CALLOUT_CLASS_MAP[tone]])}>
      <span className={classes.calloutIcon}>{icon}</span>
      <div>{children}</div>
    </div>
  );
}

function GuideSteps({ classes, steps }) {
  return (
    <div className={classes.steps}>
      {steps.map((step, index) => (
        <div className={classes.step} key={`${step.title}-${index}`}>
          <div className={classes.stepNum}>{index + 1}</div>
          <div className={classes.stepBody}>
            <div className={classes.stepTitle}>{step.title}</div>
            <div className={classes.stepDesc}>{step.description}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function GuideBadge({ classes, tone, children, style }) {
  return (
    <span className={clsx(classes.badge, tone ? classes[BADGE_CLASS_MAP[tone]] : null)} style={style}>
      {children}
    </span>
  );
}

const Guide = () => {
  const classes = useStyles();
  const guideRef = useRef(null);
  const flatTocItems = useMemo(() => TOC_SECTIONS.flatMap((section) => section.items), []);
  const [activeSection, setActiveSection] = useState(flatTocItems[0]?.id || 'connexion');

  useEffect(() => {
    PartnerPageService.loadGuide();
  }, []);

  useEffect(() => {
    const root = guideRef.current;

    if (!root || typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
      return undefined;
    }

    const anchors = Array.from(root.querySelectorAll('.guide-section-anchor'));

    if (!anchors.length) {
      return undefined;
    }

    const syncHashState = () => {
      const hash = window.location.hash.replace('#', '');

      if (!hash) {
        return;
      }

      const target = document.getElementById(hash);

      if (target) {
        setActiveSection(hash);
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    };

    syncHashState();

    const observer = new window.IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];

        if (visibleEntry?.target?.id) {
          setActiveSection(visibleEntry.target.id);
        }
      },
      {
        rootMargin: '-20% 0px -70% 0px',
        threshold: [0, 0.2, 0.4, 0.6, 1],
      }
    );

    anchors.forEach((anchor) => observer.observe(anchor));
    window.addEventListener('hashchange', syncHashState);

    return () => {
      observer.disconnect();
      window.removeEventListener('hashchange', syncHashState);
    };
  }, []);

  const handleTocClick = (id) => (event) => {
    event.preventDefault();
    setActiveSection(id);

    const target = document.getElementById(id);

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const cleanUrl = `${window.location.pathname}${window.location.search}#${id}`;
      window.history.replaceState(null, '', cleanUrl);
    }
  };

  return (
    <PartnerMainContainer title="Guide utilisateur">
      <div className={classes.page} ref={guideRef}>
        <div className={classes.layout}>
          <aside className={classes.toc}>
            <div className={classes.tocBody}>
              {TOC_SECTIONS.map((section) => (
                <div key={section.title}>
                  <div className={classes.tocSectionTitle}>{section.title}</div>
                  {section.items.map((item) => (
                    <a
                      key={item.id}
                      className={clsx(
                        classes.tocLink,
                        item.isSub && classes.tocLinkSub,
                        activeSection === item.id && classes.tocLinkActive,
                        'guide-toc-link'
                      )}
                      href={`#${item.id}`}
                      onClick={handleTocClick(item.id)}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              ))}
            </div>
          </aside>

          <Box>
            <div className={classes.pageHeader}>
              <h3 className={classes.pageTitle}>
                Guide <span className={classes.pageTitleAccent}>Utilisateur</span>
                <br />
                 Base de données Partenaires
              </h3>
              {/* <p className={classes.pageSub}>
                Guide clair et complet de la gestion des partenaires: import, navigation, suivi.
              </p> */}
            </div>

            <div className={classes.contentCard}>
              <span id="connexion" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Connexion & rôles</h2>
              <p className={classes.paragraph}>
                L'application partenaire s'utilise dans le PGI avec un accès selon le rôle
                connecté. Le contenu ci-dessous reprend le guide utilisateur existant dans un rendu
                clair directement intégré au module Partenaire.
              </p>
              <GuideSteps
                classes={classes}
                steps={[
                  {
                    title: 'Saisir vos identifiants',
                    description: 'Entrez votre identifiant et votre mot de passe PGI, puis validez.',
                  },
                  {
                    title: 'Ouvrir le module BDD Partenaire',
                    description: 'Accédez au BDD Partenaire puis ouvrez le guide depuis le menu dédié.',
                  },
                  {
                    title: 'Contrôler les rôles',
                    description: 'Le rôle actif détermine les actions accessibles sur les données et la configuration.',
                  },
                ]}
              />

              <h3 className={classes.subTitle}>Les deux rôles</h3>
              <div className={classes.roleGrid}>
                <div className={clsx(classes.roleCard, classes.roleCardAdmin)}>
                  <div className={classes.roleName}>
                    <span>🟠</span>
                    <span>Admin</span>
                  </div>
                  <ul className={classes.rolePerms}>
                    {[
                      'Importer des bases Excel',
                      'Synchroniser les bases Excel',
                      'Consulter toutes les données',
                      'Exporter les données',
                    ].map((label) => (
                      <li key={label} className={classes.rolePermItem}>
                        <span className={classes.rolePermBullet}>✓</span>
                        <span>{label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={clsx(classes.roleCard, classes.roleCardUser)}>
                  <div className={classes.roleName}>
                    <span>🔵</span>
                    <span>Utilisateur</span>
                  </div>
                  <ul className={classes.rolePerms}>
                    {[
                      { label: 'Importer des bases Excel'},
                      { label: 'Synchroniser les bases Excel',  disabled: true  },
                      { label: 'Consulter toutes les données' },
                      { label: 'Exporter les données'},
                    ].map((item) => (
                      <li
                        key={item.label}
                        className={clsx(classes.rolePermItem, item.disabled && classes.rolePermDisabled)}
                      >
                        <span className={classes.rolePermBullet}>{item.disabled ? '✗' : '✓'}</span>
                        <span>{item.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <GuideCallout classes={classes} tone="warn" icon="⚠">
                Les actions
                sensibles comme la synchronisation et la gestion des utilisateurs
                restent réservées aux administrateurs.
              </GuideCallout>

              <hr className={classes.sectionDivider} />

              <span id="interface" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Interface générale</h2>
              <h3 className={classes.subTitle}>La barre du haut</h3>
              <GuideTable
                classes={classes}
                headers={['Élément', 'Description']}
                rows={[
                  ['Synchroniser', 'Lance la synchronisation des périodes non alignées.'],
                  ['Importer', "Ouvre la fenêtre d'import Excel."],
                  ['Paramètres / session', 'Donne accès au compte, au guide et à la déconnexion.'],
                ]}
              />

              <h3 className={classes.subTitle}>La barre de navigation</h3>
              <div className={classes.navGrid}>
                {[
                  ['⊞', 'Tableau de bord', 'Vue synthétique, graphiques et alertes.'],
                  ['🏢', 'Partenaires', 'Liste consolidée des partenaires et de leurs informations.'],
                  ['📋', 'Marchés', 'Tous les marchés uniques avec dates, montants et situation.'],
                  ['📊', 'Évaluations', 'Suivi des évaluations par partenaire.'],
                  ['🚫', 'Contrats résiliés', 'Contrats résiliés avec leurs causes de rupture.'],
                  ['✅', 'Contrats achévés', 'Contrats arrivés à leur terme avec leurs dates de fin.'],
                  ['👥', 'Personnel', 'Personnel rattaché aux marchés et partenaires.'],
                  ['⚠', 'Multi-partenaires', 'Détection des conflits et chevauchements.'],
                  ['🗂', 'Bases de données', 'Gestion des bases importées (Admin).'],
                ].map(([icon, title, description]) => (
                  <div
                    key={title}
                    className={clsx(
                      classes.navItem,
                      ['Bases de données', 'Registre IDs', 'Configuration'].includes(title) && classes.navItemAdmin
                    )}
                  >
                    <div className={classes.navIcon}>{icon}</div>
                    <div>
                      <div className={classes.navTitle}>{title}</div>
                      <div className={classes.navDesc}>{description}</div>
                    </div>
                  </div>
                ))}
              </div>

              <hr className={classes.sectionDivider} />

              <span id="import" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Importer une base Excel</h2>
              <p className={classes.paragraph}>
                L'import est le point d'entrée des données. Il importe une base Excel structurée pour alimenter l'application et construire l'historique des partenaires, marchés et bénéficiaires.
                Les données ne sont pas chargées immédiatement après l'import du fichier, mais après synchronisation. Cela permet de contrôler le moment où les données deviennent actives dans l'application.
              </p>
              <GuideSteps
                classes={classes}
                steps={[
                  {
                    title: 'Cliquer sur Importer',
                    description: "La fenêtre d'import s'ouvre. Accessible aux rôles Admin et Utilisateur.",
                  },
                  {
                    title: 'Sélectionner le fichier',
                    description: 'Chargez un fichier .xlsx ou .xls. Le traitement démarre automatiquement.',
                  },
                  {
                    title: 'Importer la base',
                    description: "Cliquez sur 'Importer' pour lancer le processus d'importation de la base Excel. ",
                  },
                  {
                    title: 'Gestion des conflits',
                    description: 'En cas de conflits avec les données existantes, choisissez de les écraser ou le conserver.',
                  }
                ]}
              />
              <span
                id="import-structure"
                className={clsx(classes.sectionAnchor, 'guide-section-anchor')}
              />
              <h3 className={classes.subTitle}>Structure du fichier</h3>
              <p className={classes.paragraph}>
                Le fichier doit contenir une feuille commençant par <strong>BDD</strong> et les
                colonnes suivantes.
              </p>
              <div className={classes.columnsGrid}>
                {[
                  'NomPartenaire',
                  'StatutJuridique',
                  'Direction/Antenne',
                  'ContactPrincipal',
                  'EmailPartenaire',
                  'TéléphonePartenaire',
                  'NIF',
                  'STAT',
                  'NumeroMarche',
                  'Financement',
                  'Composante',
                  'ObjetContrat',
                  'MontantMarche',
                  'ZoneIntervention',
                  'DateDebut',
                  'DateFin',
                  'NombreBeneficiaires',
                  'NomPersonnel',
                  'Fonction',
                  'CIN',
                  'Date dernière évaluation',
                  "Résultat de l'évaluation",
                  'Date Rupture de contrat',
                  'Cause rupture de Contrat',
                  'EmailPersonnel',
                  'AdressePersonnel',
                  'TéléphonePersonnel2',
                  'DateNaissance',
                  'Diplôme',
                  'Filière',
                ].map((column) => (
                  <div
                    key={column}
                    className={clsx(
                      classes.columnItem,
                      ['EmailPersonnel', 'AdressePersonnel', 'TéléphonePersonnel2', 'DateNaissance', 'Diplôme', 'Filière'].includes(column) &&
                        classes.columnItemRh
                    )}
                  >
                    {column}
                  </div>
                ))}
              </div>
              <div className={classes.helperLegend}>
                Colonnes RH personnel en violet, colonnes contractuelles en bleu.
              </div>

                <hr className={classes.sectionDivider} />

              <span id="sync" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Synchroniser les bases Excel</h2>
              <p className={classes.paragraph}>
                La synchronisation est le processus permettant de mettre à jour les bases Excel structurées pour alimenter l'application et construire l'historique des partenaires, marchés et bénéficiaires.
              </p>
              <GuideSteps
                classes={classes}
                steps={[
                  {
                    title: 'Cliquer sur Synchroniser',
                    description: "La fenêtre de synchronisation s'ouvre. Accessible aux rôles Admin.",
                  },
                  {
                    title: 'Sélectionner les fichiers à synchroniser',
                    description: 'Choisissez les bases à synchroniser.',
                  },
                  {
                    title: 'Synchroniser les bases',
                    description: "Cliquez sur 'Synchroniser' pour lancer le processus de synchronisation des bases Excel. ",
                  },
                ]}
              />

              <hr className={classes.sectionDivider} />

              <span id="dashboard" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Tableau de bord</h2>
              <p className={classes.paragraph}>
                La vue d&apos;ensemble présente les indicateurs clés, les graphiques de répartition et
                les mouvements entre bases importées.
              </p>

              <h3 className={classes.subTitle}>KPIs</h3>
              <p className={classes.paragraph}>
                Les cartes du haut affichent les partenaires actifs, les marchés en cours, les évaluations manquantes et les conflits
                multi-partenaires.
              </p>

              <h3 className={classes.subTitle}>Graphiques</h3>
              <p className={classes.paragraph}>
                Les répartitions par direction, statut juridique, financement et composante offrent
                une lecture rapide des volumes.
              </p>

              <span id="chronologie" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h3 className={classes.subTitle}>Chronologie</h3>
              <GuideTable
                classes={classes}
                headers={['Onglet', 'Usage']}
                rows={[
                  ['Dates de contrat', 'Filtre les contrats actifs sur une plage temporelle.'],
                  ['Dates d’évaluation', 'Filtre les évaluations sur une plage sélectionnée.'],
                  ['Financements', 'Filtre les financements sur une plage sélectionnée.'],
                  ['bases importées', 'Affiche les périodes d’import et de synchronisation des bases.'],
                ]}
              />

              <GuideCallout classes={classes} tone="info" icon="ℹ">
                Le filtre actif est visible dans la chronologie. Utilisez Effacer ou Actualiser pour
                le réinitialiser.
              </GuideCallout>

              {/* <span id="mouvements" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h3 className={classes.subTitle}>Mouvements inter-bases</h3>
              <p className={classes.paragraph}>
                Quand deux périodes ou plus sont chargées, le système compare la plus récente à la
                précédente pour signaler les évolutions.
              </p>
              <div className={classes.badgeLine}>
                <div className={classes.badgeLineItem}>
                  <GuideBadge classes={classes} tone="new">
                    NOUVEAU
                  </GuideBadge>
                  <span>Apparu dans la période récente.</span>
                </div>
                <div className={classes.badgeLineItem}>
                  <GuideBadge classes={classes} tone="mod">
                    MODIFIÉ
                  </GuideBadge>
                  <span>Présent sur deux périodes avec un changement détecté.</span>
                </div>
              </div> */}

              <hr className={classes.sectionDivider} />

              <span id="partenaires" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Partenaires</h2>
              <p className={classes.paragraph}>
                Une ligne correspond à un partenaire consolidé sur les marchés associés.
              </p>

              <h3 className={classes.subTitle}>Filtres et tri</h3>
              <GuideTable
                classes={classes}
                headers={['Filtre', 'Description']}
                rows={[
                  ['Recherche texte', 'Cherche dans le nom du partenaire.'],
                  ['Direction', 'ARH, ARM, ARS, DIRT, DRA, DRB, DRF, DRK, DRP, DRU.'],
                  ['Financement', 'Source de financement du marché.'],
                  ['Composante', 'Composante du projet.'],
                  ['Situation', 'EN COURS / ACHÉVÉ / RÉSILIÉ.'],
                  ['Tri colonne', "Cliquez sur un en-tête pour trier."],
                ]}
              />

              <h3 className={classes.subTitle}>Regroupement</h3>
              <p className={classes.paragraph}>
                Le regroupement par direction, financement, statut juridique ou situation simplifie
                les revues métier et les synthèses.
              </p>

              <span id="fiche-part" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h3 className={classes.subTitle}>Fiche partenaire</h3>
              <GuideTable
                classes={classes}
                headers={['Section', 'Contenu']}
                rows={[
                  ['En-tête', 'statut juridique, contacts, NIF/STAT et situation globale.'],
                  ['Financements & Composantes', 'Tags visuels des financements et composantes.'],
                  ['KPIs synthèse', 'Évaluation, marchés, montant total et personnel.'],
                  ['Contrats / Marchés', 'Tableau détaillé des marchés associés.'],
                  ['Personnel', 'Liste du personnel associé avec leurs identifiants stables.'],
                  ['Historique', "Évolution du partenaire d'une base à l'autre."]
                ]}
              />

              <hr className={classes.sectionDivider} />

              <span id="marches" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Marchés</h2>
              <p className={classes.paragraph}>
                Une ligne correspond à un marché unique. 
              </p>
              <p className={classes.paragraph}>
                Les colonnes principales couvrent le numéro de marché, le partenaire, la direction,
                le financement, la composante, le montant, les dates et la situation.
              </p>

              {/* <GuideCallout classes={classes} tone="info" icon="ℹ">
                Les marchés de type infrastructure peuvent être exclus du total bénéficiaires selon
                la composante ou l&apos;objet du contrat.
              </GuideCallout> */}

              <hr className={classes.sectionDivider} />

              <span
                id="evaluations"
                className={clsx(classes.sectionAnchor, 'guide-section-anchor')}
              />
              <h2 className={classes.sectionTitle}>Évaluations</h2>
              <p className={classes.paragraph}>
                La section consolide une évaluation par partenaire, en priorisant la plus récente.
              </p>
              <GuideTable
                classes={classes}
                headers={['Vue', 'Description']}
                rows={[
                  ['Cartes', "Cartes visuelles avec l'historique des évaluations successives."],
                  ['Tableau', 'Liste tabulaire triable et filtrable.'],
                ]}
              />

              <h3 className={classes.subTitle}>Niveaux d&apos;évaluation</h3>
              <div className={classes.badgeRow}>
                <GuideBadge classes={classes} tone="be">
                  Bonne perf.
                </GuideBadge>
                <GuideBadge
                  classes={classes}
                  style={{ background: 'rgba(251,191,36,.15)', color: '#d97706' }}
                >
                  Moy.
                </GuideBadge>
                <GuideBadge classes={classes} tone="res">
                  Insuffisant
                </GuideBadge>
                <GuideBadge classes={classes} tone="bp">
                  Acceptable
                </GuideBadge>
                <GuideBadge
                  classes={classes}
                  style={{
                    background: 'rgba(248,113,113,.15)',
                    color: '#dc2626',
                    border: '1px dashed rgba(220,38,38,.45)',
                  }}
                >
                  ⚠ Manquant
                </GuideBadge>
              </div>

              {/* <GuideCallout classes={classes} tone="tip" icon="💡">
                En vue cartes, l&apos;historique des évaluations reste visible dès que plusieurs
                périodes ont été chargées.
              </GuideCallout> */}

              <hr className={classes.sectionDivider} />

              <span id="resilies" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Contrats Résiliés & Achévés</h2>
              <p className={classes.paragraph}>
                Le menu Contrats résiliés regroupe les marchés classés RÉSILIÉ, avec leurs causes de rupture
                quand elles sont disponibles. Tandis que le menu Contrats achevés liste les marchés classés ACHÉVÉ, avec leurs dates de fin.
              </p>
              <GuideTable
                classes={classes}
                headers={['Règle', 'Résultat']}
                rows={[
                  ['Cause de rupture renseignée', 'RÉSILIÉ'],
                  ['Date de rupture présente', 'RÉSILIÉ'],
                  ['Date de fin passée', 'ACHÉVÉ'],
                  ['Aucun des cas précédents', 'EN COURS'],
                ]}
              />

              {/* <GuideCallout classes={classes} tone="tip" icon="💡">
                Utilisez le recalcul des situations en configuration si le résultat semble
                incohérent après un import.
              </GuideCallout> */}

              <hr className={classes.sectionDivider} />

              <span id="personnel" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Personnel</h2>
              <p className={classes.paragraph}>
                La vue Personnel sert d'outil de contrôle. Une même personne peut apparaître
                plusieurs fois si elle est liée à plusieurs marchés.
              </p>
              <p className={classes.paragraph}>
                Chaque personne reçoit un identifiant stable{' '}
                <GuideBadge classes={classes} tone="peid">
                  PE-00001
                </GuideBadge>
                .
              </p>

              <h3 className={classes.subTitle}>Fiche personnel</h3>
              <GuideTable
                classes={classes}
                headers={['Section', 'Contenu']}
                rows={[
                  ['En-tête', 'Nom, PEID, fonction, CIN et situation globale.'],
                  ['Informations personnelles', 'Email, téléphone, adresse, date de naissance, diplôme et filière.'],
                  ['KPIs', 'Nombre de partenaires, marchés liés, alerte conflit et bases.'],
                  ['Alerte conflit', 'Affichée si la personne est détectée en multi-partenaires.'],
                  ['Partenaires & Contrats', 'Partenaires associés, dates et montants.'],
                  ['Présence inter-bases', 'Timeline de présence sur les différentes bases.'],
                ]}
              />

              <p className={classes.paragraph}>
                Des exports Excel et PDF peuvent être proposés depuis la fiche détaillée.
              </p>

              <hr className={classes.sectionDivider} />

              <span id="multi" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Multi-partenaires</h2>
              <p className={classes.paragraph}>
                Le module détecte automatiquement toute personne présente dans le personnel de
                plusieurs partenaires distincts.
              </p>

              <div className={classes.featureBlock}>
                <h4 className={classes.minorTitle}>
                  <GuideBadge classes={classes} tone="multi">
                    CHEVAUCHEMENT
                  </GuideBadge>{' '}
                  Chevauchement de dates
                </h4>
                <p className={classes.paragraph}>
                  Une même personne est liée à deux partenaires différents sur des périodes qui se
                  superposent.
                </p>
              </div>

              <div className={classes.featureBlock}>
                <h4 className={classes.minorTitle}>
                  <GuideBadge classes={classes} tone="ci">
                    CI + Personnel
                  </GuideBadge>{' '}
                  Consultant individuel et personnel simultané
                </h4>
                <p className={classes.paragraph}>
                  Une même personne est à la fois consultant individuel et personnel d&apos;un autre
                  partenaire, ce qui nécessite une vérification métier.
                </p>
              </div>

              <h3 className={classes.subTitle}>Comment fonctionne la détection</h3>
              <p className={classes.paragraph}>
                Le CIN est la clé d&apos;identification principale. En son absence, le système s&apos;appuie
                sur un nom normalisé. La fusion d&apos;alias permet de relier des orthographes trop
                différentes.
              </p>

              <GuideCallout classes={classes} tone="warn" icon="⚠">
                Un conflit détecté n&apos;est pas automatiquement une fraude. Il s&apos;agit d&apos;une alerte à
                analyser.
              </GuideCallout>

              {/* <hr className={classes.sectionDivider} />

              <span id="ids" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Registre des IDs stables</h2>
              <p className={classes.paragraph}>
                Chaque partenaire reçoit un identifiant permanent{' '}
                <GuideBadge classes={classes} tone="pid">
                  P-00001
                </GuideBadge>{' '}
                et chaque membre du personnel un{' '}
                <GuideBadge classes={classes} tone="peid">
                  PE-00001
                </GuideBadge>
                .
              </p>

              <h3 className={classes.subTitle}>Pourquoi les IDs stables</h3>
              <p className={classes.paragraph}>
                Ils empêchent la création de doublons quand un nom varie légèrement entre plusieurs
                bases importées.
              </p>

              <h3 className={classes.subTitle}>Le panneau Registre IDs</h3>
              <ul className={classes.bulletList}>
                <li>L&apos;ID stable et le nom consolidé.</li>
                <li>Le nombre d&apos;alias rattachés.</li>
                <li>Un indicateur de conflit si la personne est en alerte.</li>
                <li>Un raccourci vers la fiche concernée.</li>
                <li>Une recherche par ID ou par nom.</li>
              </ul>

              <span id="fusion" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h3 className={classes.subTitle}>Fusion de noms</h3>
              <GuideSteps
                classes={classes}
                steps={[
                  {
                    title: 'Saisir le nom principal',
                    description: 'Choisissez le nom de référence à conserver.',
                  },
                  {
                    title: 'Saisir l’alias à fusionner',
                    description: 'Indiquez le nom alternatif qui doit hériter du même ID.',
                  },
                  {
                    title: 'Valider la fusion',
                    description: 'Le système rattache immédiatement les données existantes au même identifiant stable.',
                  },
                ]}
              />

              <GuideCallout classes={classes} tone="info" icon="ℹ">
                La fusion est persistante dans la session. Sauvegardez ensuite pour conserver le
                registre dans un export complet.
              </GuideCallout> */}

              <hr className={classes.sectionDivider} />

              <span id="bases" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Bases de données</h2>
              <p className={classes.paragraph}>
                Cette section liste les bases importées avec leurs statistiques principales et les
                outils de comparaison.
              </p>
              <GuideTable
                classes={classes}
                headers={['Action', 'Description']}
                rows={[
                  ['Importer', "Raccourci vers la fenêtre d'import."],
                  ['Supprimer', "Retire une base de l'historique sans effacer les autres."],
                ]}
              />

              <h3 className={classes.subTitle}>Comparaison inter-bases</h3>
              <ul className={classes.bulletList}>
                <li>Partenaires nouveaux.</li>
                <li>Partenaires sortis.</li>
                <li>Partenaires modifiés avec le détail des changements.</li>
                <li>Nombre de partenaires stables.</li>
                <li>Journal des marchés nouveaux, résiliés ou terminés.</li>
              </ul>

              <hr className={classes.sectionDivider} />

              <span id="badges" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Badges & indicateurs</h2>
              <GuideTable
                classes={classes}
                headers={['Badge', 'Signification']}
                rows={[
                  [<GuideBadge classes={classes} tone="enc">EN COURS</GuideBadge>, 'Contrat actif sans rupture détectée.'],
                  [<GuideBadge classes={classes} tone="fin">ACHEVÉ</GuideBadge>, 'Contrat arrivé à échéance.'],
                  [<GuideBadge classes={classes} tone="res">RÉSILIÉ</GuideBadge>, 'Contrat interrompu avant terme.'],
                  [<GuideBadge classes={classes} tone="new">NOUVEAU</GuideBadge>, 'Partenaire apparu dans la période récente.'],
                  [<GuideBadge classes={classes} tone="mod">MODIFIÉ</GuideBadge>, 'Partenaire présent sur deux périodes avec changement.'],
                  [<GuideBadge classes={classes} tone="multi">MULTI</GuideBadge>, 'Conflit multi-partenaires détecté.'],
                  [<GuideBadge classes={classes} tone="ci">CI</GuideBadge>, 'Consultant individuel.'],
                  [<GuideBadge classes={classes} tone="pid">P-00001</GuideBadge>, 'Identifiant stable du partenaire.'],
                  [<GuideBadge classes={classes} tone="peid">PE-00001</GuideBadge>, "Identifiant stable d'un membre du personnel."],
                  [<GuideBadge classes={classes} tone="be">Bonne perf.</GuideBadge>, 'Évaluation positive.'],
                  [
                    <GuideBadge
                      classes={classes}
                      style={{ background: 'rgba(251,191,36,.15)', color: '#d97706' }}
                    >
                      Moy.
                    </GuideBadge>,
                    'Performance moyenne.',
                  ],
                  [<GuideBadge classes={classes} tone="bp">Acceptable</GuideBadge>, 'Évaluation acceptable ou satisfaisante.'],
                  [
                    <GuideBadge
                      classes={classes}
                      style={{
                        background: 'rgba(248,113,113,.15)',
                        color: '#dc2626',
                        border: '1px dashed rgba(220,38,38,.45)',
                      }}
                    >
                      ⚠ Manquant
                    </GuideBadge>,
                    'Aucune évaluation renseignée pour un partenaire en cours.',
                  ],
                ]}
              />

              <hr className={classes.sectionDivider} />

              <span id="colonnes" className={clsx(classes.sectionAnchor, 'guide-section-anchor')} />
              <h2 className={classes.sectionTitle}>Colonnes Excel</h2>
              <GuideTable
                classes={classes}
                headers={['Colonne', 'Type', 'Description']}
                rows={[
                  ['NomPartenaire', 'Texte', "Nom officiel du partenaire — clé d'identification principale."],
                  ['StatutJuridique', 'Texte', 'ONG, ASSOCIATION, ENTREPRISE, CONSULTANT INDIVIDUEL, etc.'],
                  ['Direction/Antenne', 'Texte', 'Code direction : ARH, ARM, ARS, DIRT, DRA, DRB, DRF, DRK, DRP, DRU.'],
                  ['NumeroMarche', 'Texte', 'Identifiant unique du marché.'],
                  ['Financement', 'Texte', 'Source de financement du marché.'],
                  ['Composante', 'Texte', 'Composante du projet.'],
                  ['MontantMarche', 'Nombre', 'Montant en MGA.'],
                  ['DateDebut / DateFin', 'Date', 'Dates de début et fin du contrat.'],
                  ['NombreBeneficiaires', 'Nombre entier', 'Nombre de ménages bénéficiaires.'],
                  ['NomPersonnel', 'Texte', 'Nom du membre du personnel associé au marché.'],
                  ['Fonction', 'Texte', "Rôle du personnel normalisé à l'import."],
                  ['CIN', 'Texte', "Clé principale pour l'identification du personnel."],
                  ["Résultat de l'évaluation", 'Texte', 'Valeur libre normalisée automatiquement.'],
                  ['Date dernière évaluation', 'Date', 'Date de la dernière évaluation formelle.'],
                  ['Date Rupture de contrat', 'Date', 'Si renseignée, le marché est classé RÉSILIÉ.'],
                  ['Cause rupture de Contrat', 'Texte', 'Motif de résiliation.'],
                  ['EmailPersonnel', 'Texte', 'Email du membre du personnel.'],
                  ['TéléphonePersonnel2', 'Texte', 'Téléphone du membre du personnel.'],
                  ['AdressePersonnel', 'Texte', 'Adresse du membre du personnel.'],
                  ['DateNaissance', 'Date', "Date de naissance avec âge calculé automatiquement."],
                  ['Diplôme', 'Texte', 'Niveau de diplôme du personnel.'],
                  ['Filière', 'Texte', "Filière d'études du personnel."],
                ]}
              />
            </div>
          </Box>
        </div>
      </div>
    </PartnerMainContainer>
  );
};

export default Guide;
